import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { changeLanguage, getBrowserLanguage } from '@/i18n';

export type Theme = 'dark' | 'light' | 'system';

// Which UI to render. 'v1' = the live (classic) app. 'v2' = the redesign.
// V2 is gated to the A/B cohort (abGroup 14–15) — see src/v2/canUseV2. `null` means
// "no explicit choice yet", so cohort users fall back to the V2 default.
export type UiVersion = 'v1' | 'v2';

// V2-only client preferences (persisted; additive — unused by the V1 UI).
export interface V2Prefs {
  emailNotifications: boolean;
  autoplayPreviews: boolean;
  captionsDefault: boolean;
  defaultOrientation: 'landscape' | 'portrait';
}

// All 30 languages matching the mobile app
export type Language = 
  | 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ja' | 'ko' | 'zh' | 'ar' | 'tr' | 'ru'
  | 'bg' | 'cs' | 'da' | 'el' | 'fi' | 'he' | 'hi' | 'hu' | 'id' | 'lt' | 'nl' | 'no'
  | 'pl' | 'ro' | 'sv' | 'th' | 'uk' | 'vi';

interface SettingsState {
  theme: Theme;
  language: Language;
  // null = not explicitly chosen → cohort users default to V2 (see resolveUiVersion).
  uiVersion: UiVersion | null;
  v2Prefs: V2Prefs;
  _hasHydrated: boolean;

  // Actions
  setTheme: (theme: Theme) => void;
  setLanguage: (language: Language) => void;
  setUiVersion: (uiVersion: UiVersion) => void;
  setV2Pref: <K extends keyof V2Prefs>(key: K, value: V2Prefs[K]) => void;
  setHasHydrated: (state: boolean) => void;
}

// All 30 languages with native names and flags (matching mobile app)
export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'lt', name: 'Lietuvių', flag: '🇱🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
] as const;

// Get initial language - use browser language as default
const getInitialLanguage = (): Language => {
  // Check if there's a stored preference
  try {
    const stored = localStorage.getItem('vivideo-settings');
    if (stored) {
      const settings = JSON.parse(stored);
      if (settings.state?.language) {
        return settings.state.language as Language;
      }
    }
  } catch (e) {
    // Ignore errors
  }
  
  // Fall back to browser language detection
  return getBrowserLanguage() as Language;
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      theme: 'dark',
      language: getInitialLanguage(),
      uiVersion: null, // unset → cohort (abGroup 15) defaults to V2, others to V1
      v2Prefs: {
        emailNotifications: true,
        autoplayPreviews: true,
        captionsDefault: true,
        defaultOrientation: 'landscape',
      },
      _hasHydrated: false,

      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document (for future light mode support)
        document.documentElement.setAttribute('data-theme', theme);
      },

      setLanguage: (language) => {
        set({ language });
        // Sync with i18next
        changeLanguage(language);
      },

      setUiVersion: (uiVersion) => {
        set({ uiVersion });
      },

      setV2Pref: (key, value) => {
        set((state) => ({ v2Prefs: { ...state.v2Prefs, [key]: value } }));
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'vivideo-settings',
      version: 1,
      // v0 persisted everyone as uiVersion:'v1' (the old global default). Reset it
      // to null so the new cohort gate takes over: group-15 users default to V2,
      // everyone else stays on V1. Explicit choices made post-migration persist.
      migrate: (persisted: unknown, version: number) => {
        const state = (persisted ?? {}) as Partial<SettingsState>;
        if (version < 1) state.uiVersion = null;
        return state as SettingsState;
      },
      onRehydrateStorage: () => (state) => {
        // After hydration, sync language with i18next
        if (state?.language) {
          changeLanguage(state.language);
        }
        state?.setHasHydrated(true);
      },
    }
  )
);

// App version constant
export const APP_VERSION = '1.0.0';
